
import { useState, useEffect, useCallback } from 'react';
import { Trip } from '@/types/itinerary';
import { useTransportationEvents } from './useTransportationEvents';
import { useTransportationForm } from './useTransportationForm';
import { useLocalStorage } from './useLocalStorage';

/**
 * Main hook for managing transportation details in a trip
 */
export const useTransportation = (trip: Trip | null, isAuthorized: boolean) => {
  const { 
    transportationEvents,
    isLoading,
    error,
    fetchTransportationEvents,
    clearError
  } = useTransportationEvents(trip);
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  const {
    outboundDetails,
    returnDetails,
    handleOutboundChange,
    handleReturnChange,
    saveOutboundDetails,
    saveReturnDetails
  } = useTransportationForm(trip, fetchTransportationEvents);

  // Initialize form data from local storage on component mount
  useEffect(() => {
    if (trip?.id) {
      fetchTransportationEvents();
    }
  }, [trip?.id, fetchTransportationEvents]);

  // Function to retry loading when offline or error
  const retryLoading = useCallback(() => {
    clearError();
    fetchTransportationEvents();
  }, [clearError, fetchTransportationEvents]);
  
  // Open the add transportation dialog
  const openAddTransportationDialog = useCallback(() => {
    setIsAddDialogOpen(true);
  }, []);
  
  // Close the add transportation dialog
  const closeAddTransportationDialog = useCallback(() => {
    setIsAddDialogOpen(false);
    // Refresh transportation events after dialog is closed
    fetchTransportationEvents();
  }, [fetchTransportationEvents]);
  
  // Custom save functions that close dialog and refresh after saving
  const handleSaveOutboundDetails = useCallback(async () => {
    await saveOutboundDetails();
    fetchTransportationEvents();
  }, [saveOutboundDetails, fetchTransportationEvents]);
  
  const handleSaveReturnDetails = useCallback(async () => {
    await saveReturnDetails();
    fetchTransportationEvents();
  }, [saveReturnDetails, fetchTransportationEvents]);

  return {
    outboundDetails,
    returnDetails,
    transportationEvents,
    isLoading,
    error,
    isAddDialogOpen,
    handleOutboundChange,
    handleReturnChange,
    saveOutboundDetails: handleSaveOutboundDetails,
    saveReturnDetails: handleSaveReturnDetails,
    fetchTransportationEvents,
    retryLoading,
    openAddTransportationDialog,
    closeAddTransportationDialog
  };
};
